import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import uuid from 'uuid/v1';
import { DASHBOARD_RANGE_PICKER_PRESETS as PRESETS } from 'taboola-ultimate-ui';
import { FORM_MODES } from 'config/formModes';
import { START_DATE, END_DATE, DATE_PRESET } from 'modules/campaigns/constants/queryParams';
import { REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/hooks';
import {
    METAMODEL_FILTER_TYPE,
    TIME_UNITS_GROUP_NAME,
    transformDynamicReportFromGw,
} from 'modules/campaigns/modules/campaigns-reports/services/dynamicReportsService';
import { NEW_DYNAMIC_REPORT_ID } from 'modules/scheduled-reports/config/initialValues/newDynamicReportId';
import { REPORT_SCHEDULE_ROUTE } from 'modules/scheduled-reports/config/routes/module';
import { DYNAMIC_REPORT_MODE } from 'modules/scheduled-reports/hooks/dynamicReportModeParam';
import { META_MODEL_TYPE_PERFORMANCE } from 'modules/scheduled-reports/hooks/useDynamicReportMetaModel';
import { useMetaModelExcludeNodeList } from 'modules/scheduled-reports/hooks/useMetaModelExcludeNodeList';
import {
    fetchAndTransformMetaModel,
    getMetaModelQueryKey,
    METAMODEL_QUERY_OPTIONS,
} from 'modules/scheduled-reports/utils/fetchAndTransformMetaModel';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { mergeQueryParams } from 'modules/taboola-common-frontend-modules/query-params';
import { useDynamicReportApi } from 'services/api/dynamicReportApi/useDynamicReportApi';
import { FILTER_OPERATOR } from '../../../campaigns/modules/campaigns-reports/config/filters/filterOperator';
import { CHAT_AGENT_ACTION_TYPE, CHAT_AGENT_MESSAGE_SOURCE } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT_TYPE } from '../config/chatAgentComponentType';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { formatActionButton } from '../utils/actionButtonUtils';
import { formatAction } from '../utils/eventActionsUtils';
import { enrichDynamicReportWithLabels } from '../utils/reportConfigUtils';

const extractDateParamsFromFilters = enrichedConfig => {
    const timeUnitFilter = enrichedConfig.filters?.values?.find(filter =>
        filter.nodeReference?.name?.includes(TIME_UNITS_GROUP_NAME)
    );

    if (!timeUnitFilter) {
        return {};
    }

    // Custom date range
    if (
        timeUnitFilter.filterType === METAMODEL_FILTER_TYPE.VALUE_FILTER &&
        timeUnitFilter.filterOperator === FILTER_OPERATOR.BETWEEN &&
        timeUnitFilter.values?.length === 2
    ) {
        return {
            [START_DATE]: timeUnitFilter.values[0],
            [END_DATE]: timeUnitFilter.values[1],
            [DATE_PRESET]: PRESETS.CUSTOM,
        };
    }

    // Preset date range
    if (
        timeUnitFilter.filterType === METAMODEL_FILTER_TYPE.PREDEFINED_FILTER &&
        timeUnitFilter.filterOperator === FILTER_OPERATOR.EQUALS &&
        timeUnitFilter.values?.length === 1
    ) {
        return {
            [DATE_PRESET]: timeUnitFilter.values[0],
        };
    }

    return {};
};

const buildNavigationUrl = enrichedConfig => {
    const dateParams = extractDateParamsFromFilters(enrichedConfig);

    return `${REPORT_SCHEDULE_ROUTE}${mergeQueryParams('', {
        [REPORT_ID]: NEW_DYNAMIC_REPORT_ID,
        [DYNAMIC_REPORT_MODE]: FORM_MODES.EDIT,
        ...dateParams,
    })}`;
};

const prepareReportData = ({ reportConfig, metaModel, formatMessage, accountId }) => {
    const enrichedConfig = enrichDynamicReportWithLabels(reportConfig, metaModel);
    const transformedReport = transformDynamicReportFromGw(enrichedConfig, { formatMessage });

    const reportData = {
        ...transformedReport,
        id: NEW_DYNAMIC_REPORT_ID,
        name:
            transformedReport.name ||
            formatMessage({
                id: 'dynamic.reports.dynamic.report.new.report.label',
                defaultMessage: 'New report',
            }),
        numericAccountId: transformedReport.numericAccountId || `${accountId}`,
    };

    return { reportData, enrichedConfig };
};

export const useReportingTools = ({ sendResponseToChatAgent }) => {
    const { formatMessage } = useIntl();
    const account = useAccount();
    const { id: accountId, accountName } = account;
    const queryClient = useQueryClient();
    const dynamicReportApi = useDynamicReportApi();
    const excludeNodeList = useMetaModelExcludeNodeList();

    const navigateToDynamicReport = useCallback(
        async ({ reportConfig }) => {
            if (!reportConfig || typeof reportConfig !== 'object') {
                return false;
            }

            try {
                const metaModel = await queryClient.fetchQuery(
                    getMetaModelQueryKey(accountName),
                    () =>
                        fetchAndTransformMetaModel({
                            dynamicReportApi,
                            accountName,
                            metaModelType: META_MODEL_TYPE_PERFORMANCE,
                            excludeNodeList,
                        }),
                    METAMODEL_QUERY_OPTIONS
                );

                const { reportData, enrichedConfig } = prepareReportData({
                    reportConfig,
                    metaModel,
                    formatMessage,
                    accountId,
                });

                const navigationUrl = buildNavigationUrl(enrichedConfig);

                const buttonConfig = formatActionButton({
                    icon: 'link',
                    title: formatMessage({
                        id: 'abby.navigateToDynamicReport.button',
                        defaultMessage: 'View report',
                    }),
                    postIcon: 'arrow-right',
                    flow: {
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.SET_DYNAMIC_REPORT_DATA,
                                data: {
                                    reportId: NEW_DYNAMIC_REPORT_ID,
                                    accountId: `${accountId}`,
                                    reportData,
                                },
                            }),
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.NAVIGATE,
                                data: {
                                    url: navigationUrl,
                                },
                            }),
                        ],
                    },
                });

                const message = {
                    id: uuid(),
                    componentData: {
                        value: buttonConfig,
                        componentName: CHAT_AGENT_COMPONENT_TYPE.ACTION_BUTTON,
                    },
                    flows: [
                        {
                            actions: [
                                formatAction({
                                    type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                    data: {
                                        value: buttonConfig,
                                        source: CHAT_AGENT_MESSAGE_SOURCE.AGENT,
                                    },
                                }),
                            ],
                        },
                    ],
                };

                sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });

                return true;
            } catch (error) {
                console.error('Failed to navigate to dynamic report:', error);
                return false;
            }
        },
        [sendResponseToChatAgent, formatMessage, accountId, accountName, queryClient, dynamicReportApi, excludeNodeList]
    );

    const downloadCsv = useCallback(
        async ({ csvData, baseFilename }) => {
            if (!csvData || typeof csvData !== 'string') {
                return false;
            }

            if (csvData.length > 50 * 1024 * 1024) {
                return false;
            }

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `${baseFilename || 'report'}_${timestamp}.csv`;

            const buttonConfig = formatActionButton({
                icon: 'page',
                title: formatMessage({ id: 'abby.downloadCsv.button', defaultMessage: 'Download CSV' }),
                postIcon: 'download',
                flow: {
                    actions: [
                        formatAction({
                            type: CHAT_AGENT_ACTION_TYPE.DOWNLOAD_FILE,
                            data: {
                                rawData: JSON.stringify({
                                    filename,
                                    content: csvData,
                                    mimeType: 'text/csv',
                                }),
                            },
                        }),
                    ],
                },
            });

            const message = {
                id: uuid(),
                componentData: {
                    value: buttonConfig,
                    componentName: CHAT_AGENT_COMPONENT_TYPE.ACTION_BUTTON,
                },
                flows: [
                    {
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                data: {
                                    value: buttonConfig,
                                    source: CHAT_AGENT_MESSAGE_SOURCE.AGENT,
                                },
                            }),
                        ],
                    },
                ],
            };

            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });

            return true;
        },
        [sendResponseToChatAgent, formatMessage]
    );

    return useMemo(
        () => ({
            downloadCsv,
            navigateToDynamicReport,
        }),
        [downloadCsv, navigateToDynamicReport]
    );
};
